import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { AdminHTTPService } from './admin-http/admin-http.service';
import { AuthService, UserDataModel } from '../../auth';
import { ResponseModel } from '@modules/auth/_models/response.model';
import { USER_TOKEN_KEY } from '@shared/constants';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;

  constructor(private adminHttpService: AdminHTTPService,
              private authService: AuthService,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  createGroup({groupName, description, groupPicture, groupPictureFileName, isPrivate}: {
    groupName: string,
    description: string,
    groupPicture: string,
    groupPictureFileName: string,
    isPrivate: boolean
  }) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.createGroup({
      groupName,
      description,
      groupPicture,
      groupPictureFileName,
      isPrivate,
    }).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  loginAsUser(behalfOfUser: string): Observable<ResponseModel<UserDataModel>> {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.loginAsUser(behalfOfUser).pipe(
      map(userResp => {
        if (!userResp.succeeded) {
          return userResp;
        }
        const user = userResp.data;
        localStorage.setItem(USER_TOKEN_KEY, user.jwToken);
        this.authService.currentUserValue = user;
        return userResp;
      }),
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  getAllRequests(pageNumber: number, pageSize: number, searchKey: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.getAllRequests(pageNumber, pageSize, searchKey).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  getGroupAllUsers(pageNumber: number, pageSize: number, groupId: number) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.getGroupAllUsers(pageNumber, pageSize, groupId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  addUserToGroup(groupId: number, userId: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.addUserToGroup(groupId, userId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  removeUserFromGroup(groupId: number, userId: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.removeUserFromGroup(groupId, userId).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  joinRequestResult(id: number, isApproved: boolean) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.joinRequestResult(id, isApproved).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  getAllUsers(pageNumber: number, pageSize: number, searchKey: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.getAllUsers(pageNumber, pageSize, searchKey).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  changeActivationOfUser(email: string, enabled: boolean) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.changeActivationOfUser(email, enabled).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  resetPassword(email: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.resetPassword(email).pipe(
      catchError(err => {
        console.log('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  deleteUserProfile(userId: string) {
    this.isLoadingSubject.next(true);
    return this.adminHttpService.deleteUserProfile(userId).pipe(
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }
}
